/**
 * Teams Layout Component
 *
 * Provides a simplified layout for the Teams Tab experience.
 * Unlike the web app layout, this doesn't include header/footer navigation
 * as Teams provides its own navigation chrome.
 */

import React from 'react';
import { Outlet } from '@tanstack/react-router';
import {
  makeStyles,
  tokens,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useTeams } from '@/hooks/useTeams';
import { useTeamsAuth } from '@/hooks/useTeamsAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: tokens.spacingVerticalL,
  },
  main: {
    flex: 1,
    padding: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,

    // Mobile responsive padding
    '@media (max-width: 768px)': {
      padding: tokens.spacingVerticalS,
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
    },
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXL,
    gap: tokens.spacingVerticalL,
  },
});

export const TeamsLayout: React.FC = () => {
  const styles = useStyles();
  const teamsContext = useTeams();
  const teamsAuth = useTeamsAuth();

  // Handle null returns when hooks are called outside their providers
  const isInitializing = teamsContext?.isInitializing ?? false;
  const teamsError = teamsContext?.error ?? null;
  const isAuthLoading = teamsAuth?.isLoading ?? false;
  const isAuthenticated = teamsAuth?.isAuthenticated ?? false;

  // Show loading state while Teams SDK initializes
  if (isInitializing) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="extra-large" label="Initializing Teams app..." />
      </div>
    );
  }

  // Show error if Teams initialization failed
  if (teamsError) {
    return (
      <div className={styles.errorContainer}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Failed to initialize Teams</MessageBarTitle>
            {teamsError.message}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // Show loading state while authenticating
  if (isAuthLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="extra-large" label="Authenticating..." />
      </div>
    );
  }

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={styles.errorContainer}>
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Authentication Required</MessageBarTitle>
            Please ensure you are signed in to Teams to use this app.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // Render the main content
  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};
